import { useEffect, useState } from "react";
import { api, formatCOP, formatNumber } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileSearch, AlertTriangle, CheckCircle2 } from "lucide-react";

/** Cruza los lotes de un contenedor contra el Excel original y corrige cantidades infladas. */
export default function ConciliarContenedorDialog({ contenedor, open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setFile(null); setMotivo(""); setPreview(null); } }, [open]);

  const fd = () => { const f = new FormData(); f.append("file", file); if (motivo.trim()) f.append("motivo", motivo.trim()); return f; };

  const analizar = async () => {
    if (!file) return toast.error("Selecciona el Excel del contenedor");
    setLoading(true);
    try {
      const { data } = await api.post(`/productos/_/contenedores/${contenedor.id}/conciliar-excel/preview`, fd(), { timeout: 120000 });
      setPreview(data);
    } catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "No se pudo analizar el Excel"); }
    finally { setLoading(false); }
  };

  const aplicar = async () => {
    if (motivo.trim().length < 5) return toast.error("Escribe el motivo (mínimo 5 caracteres)");
    setSaving(true);
    try {
      const { data } = await api.post(`/productos/_/contenedores/${contenedor.id}/conciliar-excel`, fd(), { timeout: 120000 });
      toast.success(`Conciliado: ${data.desajustes} lotes corregidos · ${formatNumber(Math.abs(data.unidades_diff))} und. · ${formatCOP(Math.abs(data.valor_diff))}`);
      onDone?.(); onClose();
    } catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "No se pudo aplicar"); }
    finally { setSaving(false); }
  };

  if (!contenedor) return null;
  const ok = preview && preview.desajustes === 0;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="conciliar-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><FileSearch className="w-5 h-5 text-primary" /> Conciliar con Excel · {contenedor.referencia}</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">Sube el mismo Excel que importaste. El sistema compara la cantidad de cada lote contra el documento y te muestra las diferencias. Al aplicar, cada lote queda exactamente con la cantidad del Excel y el stock del producto se ajusta por la diferencia.</p>
        </DialogHeader>
        <div className="mt-2">
          <Label>Excel del contenedor (.xlsx)</Label>
          <Input type="file" accept=".xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} className="h-11 mt-1" data-testid="conciliar-file" />
        </div>
        {!preview && (
          <Button onClick={analizar} disabled={loading || !file} variant="outline" className="mt-3 h-11" data-testid="conciliar-analizar-btn">
            {loading ? "Analizando..." : "Comparar lotes vs. Excel"}
          </Button>
        )}
        {preview && (
          <div className="mt-3 space-y-3" data-testid="conciliar-preview">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              <Kpi label="Unidades Excel" value={formatNumber(preview.unidades_excel)} />
              <Kpi label="Unidades en lotes" value={formatNumber(preview.unidades_lotes)} warn={preview.unidades_lotes !== preview.unidades_excel} />
              <Kpi label="Lotes con diferencia" value={formatNumber(preview.desajustes)} warn={preview.desajustes > 0} />
              <Kpi label="Valor de más" value={formatCOP(preview.valor_diff)} warn={preview.valor_diff !== 0} />
            </div>
            {ok ? (
              <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 rounded-md p-3" data-testid="conciliar-ok"><CheckCircle2 className="w-4 h-4" /> Todos los lotes coinciden con el Excel. No hay nada que corregir.</div>
            ) : (
              <>
                <div className="max-h-64 overflow-y-auto rounded-lg border border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0"><tr><th className="text-left p-2">Producto</th><th className="text-right p-2">Lote</th><th className="text-right p-2">Excel</th><th className="text-right p-2">Diferencia</th><th className="text-right p-2">Valor</th><th className="text-right p-2">Stock → nuevo</th></tr></thead>
                    <tbody>
                      {preview.filas.map((f) => (
                        <tr key={f.lote_id} className="border-t border-border/60" data-testid={`conciliar-fila-${f.sku}`}>
                          <td className="p-2 truncate max-w-[240px]">{f.producto_nombre}<span className="text-muted-foreground"> · {f.sku}</span></td>
                          <td className="p-2 text-right tabular-nums">{formatNumber(f.lote_recibida)}</td>
                          <td className="p-2 text-right tabular-nums font-semibold">{formatNumber(f.excel)}</td>
                          <td className={`p-2 text-right tabular-nums font-bold ${f.diff > 0 ? "text-rose-600" : "text-amber-600"}`}>{f.diff > 0 ? "+" : ""}{formatNumber(f.diff)}</td>
                          <td className="p-2 text-right tabular-nums">{formatCOP(f.valor_diff)}</td>
                          <td className="p-2 text-right tabular-nums text-muted-foreground">{formatNumber(f.stock_actual)} → {formatNumber(Math.max(0, f.stock_actual - f.diff))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(preview.sin_excel.length > 0 || preview.sin_lote.length > 0) && (
                  <div className="text-[11px] text-muted-foreground">
                    {preview.sin_excel.length > 0 && <div>{preview.sin_excel.length} lote(s) sin fila en el Excel (no se tocan): {preview.sin_excel.slice(0, 5).map((s) => s.producto_nombre).join(", ")}</div>}
                    {preview.sin_lote.length > 0 && <div>{preview.sin_lote.length} SKU(s) del Excel sin lote: {preview.sin_lote.slice(0, 8).map((s) => s.sku).join(", ")}</div>}
                  </div>
                )}
                <div>
                  <Label>Motivo *</Label>
                  <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej: importación duplicada del 26-oct sumó cantidades" className="h-11 mt-1" data-testid="conciliar-motivo" />
                </div>
                <div className="flex items-start gap-2 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-md p-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> Se ajustan lotes y stock por la diferencia; queda en auditoría y en movimientos de inventario. Los lotes sin fila en el Excel no se modifican.
                </div>
              </>
            )}
          </div>
        )}
        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={onClose} data-testid="conciliar-cancelar-btn">Cerrar</Button>
          {preview && !ok && <Button onClick={aplicar} disabled={saving} className="bg-primary" data-testid="conciliar-aplicar-btn">{saving ? "Aplicando..." : `Corregir ${preview.desajustes} lote(s)`}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, warn }) {
  return (
    <div className={`rounded-lg border p-2 ${warn ? "border-rose-300 bg-rose-50/60 dark:bg-rose-900/15" : "border-border bg-card"}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-base font-bold tabular-nums">{value}</div>
    </div>
  );
}
